"use client";
import Link from "@/components/ui/Link";
import { useEffect, useRef } from "react";
import { FEED_NAV } from "@/lib/navigation";
import { cn } from "@/lib/utils";

interface CategoryPillsProps {
  activePath?: string;
}

// Single horizontal-scroll row instead of flex-wrap. Wrap took 3-4 rows
// on mobile, pushing the actual content (sticker pack) below the fold.
// This pattern matches YouTube / Twitter / Instagram tab strips.
//
// Negative margins + matching padding let the strip extend edge-to-edge
// so the last pill isn't visually cut off by the page padding. The
// scrollbar is hidden visually but scrolling still works (touch + wheel
// + click-and-drag).
const SCROLL_STORAGE_KEY = "sticko:category-pills-scroll";

export function CategoryPills({ activePath }: CategoryPillsProps) {
  const navRef = useRef<HTMLElement>(null);

  // Persist horizontal scroll position across category navigations. Each
  // category page re-mounts CategoryPills (separate routes), which would
  // otherwise reset scrollLeft to 0 — so a user who scrolled to "Memes"
  // and clicks it would land on the new page with the strip back at
  // "For You". sessionStorage scope keeps it per-tab and clears when the
  // tab closes.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (saved !== null) {
      const value = Number(saved);
      if (Number.isFinite(value)) el.scrollLeft = value;
    }
    let rafId: number | undefined;
    const onScroll = () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, String(el.scrollLeft));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafId !== undefined) cancelAnimationFrame(rafId);
    };
  }, []);

  // Mouse-wheel → horizontal scroll. By default a vertical wheel event
  // over a horizontal-scroll container scrolls the page, not the strip,
  // so desktop users can't reach the off-screen pills. Trackpads on
  // macOS/Windows already produce deltaX for horizontal swipes, so we
  // only translate when deltaY dominates (real mouse wheel).
  // passive:false is required to call preventDefault on wheel events
  // since Chrome 73+.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      // Don't hijack if the strip can't scroll any further in the wheel
      // direction — let the page scroll normally past it.
      const atStart = el.scrollLeft <= 0 && e.deltaY < 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth && e.deltaY > 0;
      if (atStart || atEnd) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Click-and-drag horizontal scroll for desktop mouse users. Touch
  // already works natively (overflow-x-auto). We track total drag
  // distance and suppress the click on the underlying <Link> if the
  // pointer moved more than 5px between mousedown and mouseup — that way
  // a normal click navigates, but a drag never accidentally fires it.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let dragDistance = 0;
    const DRAG_THRESHOLD = 5;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      isDown = true;
      startX = e.pageX;
      startScrollLeft = el.scrollLeft;
      dragDistance = 0;
      el.style.cursor = "grabbing";
      // Prevent text-selection drag while moving the strip.
      e.preventDefault();
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      dragDistance = Math.max(dragDistance, Math.abs(dx));
      el.scrollLeft = startScrollLeft - dx;
    };
    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = "";
    };
    const onClickCapture = (e: MouseEvent) => {
      if (dragDistance > DRAG_THRESHOLD) {
        e.preventDefault();
        e.stopPropagation();
        dragDistance = 0;
      }
    };

    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", endDrag);
    // capture phase so we run before <Link>'s click handler
    el.addEventListener("click", onClickCapture, true);
    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", endDrag);
      el.removeEventListener("click", onClickCapture, true);
    };
  }, []);

  return (
    <nav
      ref={navRef}
      aria-label="Categories"
      className="-mx-4 flex cursor-grab gap-2 overflow-x-auto px-4 pb-1 select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {FEED_NAV.map((item) => {
        const isActive = activePath === item.href;
        return (
          <Link
            key={item.href}
            href={item.href}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition",
              isActive
                ? "border-ink bg-ink text-white"
                : "border-ink/80 text-ink hover:bg-muted bg-white",
            )}
          >
            {/* System emoji fonts only. Roboto's @font-face unicode-ranges
                cover 📺 and ❤, so with the inherited font stack the browser
                downloaded an extra ~17 KB Roboto subset on every page just to
                discover it has no colour emoji — a top-priority request ahead
                of the first paint. Naming only local emoji fonts here means no
                web font is consulted for these glyphs. */}
            <span
              aria-hidden
              style={{
                fontFamily: '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",emoji',
              }}
            >
              {item.emoji}
            </span>
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
